import { plainToClass } from "class-transformer";
import { LMSUserTrainingOverviewDto, LMSTrainingStatus } from "./home.objects";
import { LMSTrainingMaterialDto } from "../../settings/scripts/trainings.objects"
export class LMSHomePage {
    userTrainingsTable: any;
    allUserTrainings: LMSUserTrainingOverviewDto[];
    userTrainings: LMSUserTrainingOverviewDto[];

    private completingUserTrainingId: number | null = null;
    private statusFilter: LMSTrainingStatus | null = null;
    private static readonly imageExtensions = ["png", "jpg", "jpeg", "webp", "svg"];
    private static readonly videoExtensions = ["mp4"];
    private static readonly previewableExtensions = ["pdf", ...LMSHomePage.imageExtensions, ...LMSHomePage.videoExtensions];

    private static readonly completedSortOffset = 1e15;
    private static readonly statusConfig: { status: LMSTrainingStatus; label: string }[] = [
        { status: "pending", label: "Pending" },
        { status: "overdue", label: "Overdue" },
        { status: "complete", label: "Complete" },
        { status: "expired", label: "Expired" }
    ];

    constructor(allUserTrainingsJson: any) {
        this.allUserTrainings = plainToClass(LMSUserTrainingOverviewDto, <LMSUserTrainingOverviewDto[]>allUserTrainingsJson);
        this.userTrainings = this.allUserTrainings;

        this.initializeTable();
        this.renderProgressBar();

        $("#saveTrainingCompleteBtn").on('click', () => this.submitDialog());
        $(document).on('click', '.lms-material-view', (e) => {
            const t = $(e.currentTarget);
            const fileName = t.data('filename');
            const downloadUrl = t.data('downloadUrl');

            if (this.canPreview(fileName)) {
                this.openMaterialModal(t.data('previewUrl'), downloadUrl, fileName);
            } else {
                this.downloadFile(downloadUrl);
            }
        });

        $('#training-progress-bar').on('click', '.lms-progress-segment', (e) => {
            const status = $(e.currentTarget).data('status') as LMSTrainingStatus;
            this.toggleStatusFilter(status);
        });

        $('#reset-progress-bar').on('click', () => this.resetStatusFilter());
    }

    private initializeTable(): void {
        this.userTrainingsTable = $('#lmsUserTrainingTable').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            order: [[2, 'asc']], // not-complete trainings first (by days remaining), then completed trainings (by completion date)
            columns: [
                {
                    className: 'training-details-toggle text-center',
                    orderable: false,
                    data: null,
                    defaultContent: '<i class="fa-solid fa-angle-up"></i>',
                    width: '25px'
                },
                { data: "trainingName", title: "Training Name", className: "text-nowrap training-name" },
                {
                    data: "daysRemaining",
                    title: "Days Remaining",
                    render: (data: number, type: string, row: LMSUserTrainingOverviewDto) => {
                        if (type === 'sort' || type === 'type') {
                            return row.isComplete
                                ? LMSHomePage.completedSortOffset + new Date(row.completedAt).getTime()
                                : data;
                        }
                        return row.isComplete ? "" : data > -1 ? `${data} days` : `${-data} days overdue`;
                    }
                },
                { data: "expirationDate", title: "Expiration Date" },
                { data: "completedAt", title: "Completed Date" },
                {
                    data: "isComplete", title: "Completed",
                    render: function (data: boolean) {
                        if (data) {
                            return '<i class="fa-solid fa-check text-success text-center"></i>';
                        }
                        return '<i class="fa-solid fa-xmark text-danger text-center"></i>';
                    }
                }
            ],
            data: this.userTrainings
        });

        $('#lmsUserTrainingTable tbody').on('click', 'tr', (e) => {
            const tr = $(e.currentTarget);
            //if (tr.hasClass('child') || $(e.target).closest(".lms-complete-training-button").length) return;

            const row = this.userTrainingsTable.row(tr);
            if (!row.length) return;

            tr.toggleClass('details-open', !row.child.isShown());
            if (row.child.isShown()) {
                row.child().find('.training-details-body').slideUp(200, () => row.child.hide());
            } else {
                row.child(this.renderTrainingDetails(row.data())).show();
                row.child().find('.training-details-body').hide().slideDown(200);
            }
        })

        $('#lmsUserTrainingTable tbody').on('click', '.lms-complete-training-button', (e) => {
            e.stopPropagation();
            const tr = $(e.currentTarget).closest('tr').prev();
            const row = this.userTrainingsTable.row(tr);
            this.openDialog(row.data().id);
        })
    }

    // ===== Status Filter =====
    private toggleStatusFilter(status: LMSTrainingStatus): void {
        this.statusFilter = this.statusFilter === status ? null : status;
        this.renderProgressBar();
        this.applyStatusFilter();
        this.updateResetButtonState();
    }

    private resetStatusFilter(): void {
        if (!this.statusFilter) {
            return;
        }

        this.statusFilter = null;
        this.renderProgressBar();
        this.applyStatusFilter();
        this.updateResetButtonState();
    }

    private updateResetButtonState(): void {
        $('#reset-progress-bar').prop('disabled', !this.statusFilter);
    }

    private applyStatusFilter(): void {
        this.userTrainings = this.statusFilter
            ? this.allUserTrainings.filter(t => t.getStatus() === this.statusFilter)
            : this.allUserTrainings;

        this.userTrainingsTable.clear();
        this.userTrainingsTable.rows.add(this.userTrainings).draw();
    }

    private renderProgressBar(): void {
        const counts: Record<LMSTrainingStatus, number> = { pending: 0, overdue: 0, complete: 0, expired: 0 };
        this.allUserTrainings.forEach(t => counts[t.getStatus()]++);

        const total = this.allUserTrainings.length;

        const segments = LMSHomePage.statusConfig.map(({ status, label }) => {
            const count = counts[status];
            const percent = total > 0 ? (count / total) * 100 : 0;
            const classes = [
                'lms-progress-segment',
                `lms-progress-segment-${status}`,
                count > 0 ? 'has-count' : '',
                this.statusFilter === status ? 'active' : ''
            ].filter(c => c).join(' ');

            if (percent > 0) {
                return `<div class="${classes}" style="width:${percent}%" data-status="${status}">${count} ${label}</div>`;
            }
        }).join('');

        $('#training-progress-bar').html(`
            <div class="lms-progress-track">${segments}</div>
        `);
    }

    // ===== Training Details =====
    private renderTrainingDetails(training: LMSUserTrainingOverviewDto): string {
        const sections: string[] = [];

        if (training.instructions && training.instructions != "") {
            sections.push(`
                <div class="user-training-section">
                    <h5>Instructions</h5>
                    <p>${training.instructions}</p>
                </div>
            `);
        }

        if (training.materials.length > 0) {
            sections.push(`
                <div class="user-training-section">
                    <h5>Materials</h5>
                    <div class="materials-list">
            `);
            training.materials.forEach((material: LMSTrainingMaterialDto) => {
                const fileName = material.fileName;
                const s3Id = encodeURIComponent(material.materialLink);
                const encodedFileName = encodeURIComponent(fileName);
                const previewUrl = `/secure/lms?handler=PreviewMaterial&s3Id=${s3Id}&fileName=${encodedFileName}`;
                const downloadUrl = `/secure/lms?handler=DownloadMaterial&s3Id=${s3Id}&fileName=${encodedFileName}`;
                const iconClass = this.getFileIconClass(fileName);
                const displayName = this.getFileNameWithoutExtension(fileName);
                sections.push(`
                    <div class="lms-file-group">
                        <div class="lms-file-chip">
                            <a class="lms-file-name lms-material-view" href="javascript:void(0)" title="${fileName}"
                               data-preview-url="${previewUrl}" data-download-url="${downloadUrl}" data-filename="${fileName}">
                               <i class="${iconClass} pr-1"></i> ${displayName}
                            </a>
                        </div>
                        
                    </div>
                `);
            });
            sections.push(`
                </div>
                </div>
            `);
        }

        if (training.isComplete) {
            sections.push(`
                <button type="button" class="lms-button lms-button-primary lms-complete-training-button" disabled>Completed</button>
            `)
        } else if (!training.requiresUpload) {
            sections.push(`
                <button type="button" class="lms-button lms-button-primary lms-complete-training-button"><i id="saveTrainingSpinner" class="ctms-icon fa fa-spinner fa-spin fa-1x mr-2" style="display:none;"></i>Mark As Complete</button>
            `)
        }
        

        return `
            <div class="training-details-body">
                ${sections.join("")}
            </div>

        `
    }

    private getFileNameWithoutExtension(fileName: string): string {
        const lastDot = fileName.lastIndexOf('.');
        return lastDot > 0 ? fileName.substring(0, lastDot) : fileName;
    }

    private getFileIconClass(fileName: string): string {
        const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
        switch (ext) {
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
            case 'svg':
                return "fas fa-file-image";
            case 'pdf':
                return "fa-solid fa-file-pdf lms-red-icon";
            case 'doc':
            case 'docx':
                return "fa-solid fa-file-word lms-blue-icon";
            case 'xls':
            case 'xlsx':
            case 'csv':
                return "fa-solid fa-file-excel lms-green-icon";
            case 'ppt':
            case 'pptx':
                return "fa-solid fa-file-powerpoint lms-orange-icon";
            case 'mp3':
            case 'wav':
            case 'ogg':
                return "fa-solid fa-file-audio";
            case 'mp4':
            case 'avi':
            case 'mov':
            case 'wmv':
                return "fa-solid fa-file-video";
            default:
                return "fa-solid fa-file";
        }
    }

    private openDialog(id: number) {
        this.completingUserTrainingId = id;

        $('#attestCompletionCheckbox').prop('checked', false);
        $("#completeTrainingPassword").val("");
        $('#completeTrainingDialog .invalid-feedback').text('').hide();
        $('#completeTrainingDialog .is-invalid').removeClass('is-invalid');
        $('#completeTrainingDialog').modal('show');
    }

    private submitDialog() {
        if (!this.validate()) {
            return;
        }

        $("#saveTrainingCompleteSpinner").show();

        $.ajax({
            url: `?handler=MarkComplete&id=${this.completingUserTrainingId}`,
            method: 'POST',
            headers: { 'RequestVerificationToken': $('input[name="__RequestVerificationToken"]').val() as string },
            data: {
                password: $("#completeTrainingPassword").val()
            }
        }).done(() => {
            $("#saveTrainingCompleteSpinner").hide();
            $('#completeTrainingDialog').modal('hide');
            location.reload();
        }).fail((e) => {
            $("#saveTrainingCompleteSpinner").hide();
            const passwordEl = $("#completeTrainingPassword");

            this.markInvalid(
                passwordEl,
                e.responseJSON?.message ?? "Unable to verify your password."
            );
        });
    }

    private validate(): boolean {
        $("#completeTrainingDialog .is-invalid").removeClass("is-invalid");
        $(".invalid-feedback").text("").hide();

        let isValid = true;

        const attestEl = $("#attestCompletionCheckbox");
        if (!attestEl.is(':checked')) {
            this.markInvalid(attestEl, "Please agree to the statement to mark training as complete.");
            isValid = false;
        }

        const passEl = $("#completeTrainingPassword");
        if (!passEl.val()) {
            this.markInvalid(passEl, "Please enter your password to verify your identity.");
            isValid = false;
        }

        return isValid;
    }

    private markInvalid(element: JQuery, errorMessage: string) {
        const formGroup = element.closest('.form-group');

        if (element.hasClass("selectpicker")) {
            formGroup.find(".dropdown-toggle").addClass("is-invalid");
        } else if (element.is(":checkbox")) {
            formGroup.find(".lms-form-check").addClass("is-invalid");
        } else {
            element.addClass("is-invalid");
        }

        formGroup.find(".invalid-feedback").text(errorMessage).show();
    }

    private canPreview(fileName: string): boolean {
        const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
        return LMSHomePage.previewableExtensions.includes(ext);
    }

    private downloadFile(url: string): void {
        window.location.href = url;
    }

    private openMaterialModal(previewUrl: string, downloadUrl: string, fileName: string): void {
        const modal = $('#viewMaterialModal');
        modal.find('#viewMaterialModalLabel').text(fileName);

        const iframe = modal.find('#material-preview-iframe');
        const image = modal.find('#material-preview-image');
        const video = modal.find('#material-preview-video');

        iframe.hide().attr('src', '');
        image.hide().attr('src', '');
        video.hide().attr('src', '');

        const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
        if (ext === 'pdf') {
            iframe.attr('src', previewUrl).show();
        } else if (LMSHomePage.imageExtensions.includes(ext)) {
            image.attr('src', previewUrl).show();
        } else if (LMSHomePage.videoExtensions.includes(ext)) {
            video.attr('src', previewUrl).show();
        }

        modal.modal('show');
    }
}